/** @format */

import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@clarix/ui';

export interface SwitcherItem<K extends string = string> {
  key: K;
  label?: ReactNode;
  icon?: LucideIcon;
  count?: number;
  /** Hide the label below `sm` (icon-only on mobile). Defaults to true. */
  responsiveLabel?: boolean;
}

interface SwitcherBarProps<K extends string = string> {
  items: SwitcherItem<K>[];
  value: K;
  onChange: (key: K) => void;
  /**
   * `line` — full-width underline strip (the project-detail tab style).
   * `box`  — compact segmented control (the calendar view-switcher style).
   */
  variant?: 'line' | 'box';
  /** Right-aligned content rendered after the tabs (only meaningful for `line`). */
  children?: ReactNode;
  className?: string;
}

/**
 * The multi-section switcher — one component, two canonical looks. Replaces the
 * three ad-hoc tab styles the app grew (ProjectTabs underline, calendar
 * segmented control, etc.) so every "switch between sections/views" affordance
 * looks and behaves identically.
 */
export function SwitcherBar<K extends string = string>({
  items,
  value,
  onChange,
  variant = 'line',
  children,
  className,
}: SwitcherBarProps<K>) {
  if (variant === 'box') {
    return (
      <div
        className={cn(
          'bg-muted border-border/50 inline-flex shrink-0 rounded-lg border p-0.5',
          className,
        )}
      >
        {items.map((item) => {
          const isActive = item.key === value;
          const Icon = item.icon;
          return (
            <button
              key={item.key}
              type="button"
              onClick={() => onChange(item.key)}
              className={cn(
                'flex cursor-pointer items-center gap-1.5 rounded-md px-3.5 py-1.5 text-lg font-medium transition-all',
                isActive
                  ? 'bg-background text-foreground font-semibold shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {Icon && <Icon className="h-3.5 w-3.5" strokeWidth={1.5} />}
              {item.label}
            </button>
          );
        })}
      </div>
    );
  }

  // variant === 'line'
  return (
    <div
      className={cn(
        // items-stretch so tabs fill the bar height — the active underline then
        // sits flush on the bottom border (one line, not a floating indicator).
        'border-border bg-surface flex h-10 shrink-0 items-stretch border-b px-5',
        className,
      )}
    >
      {/* `-ml-1.5` cancels the FIRST tab's own `px-1.5`. The bar sits on `px-5` —
          the same gutter every row below it uses (PanelBreadcrumb, toolbars, table
          columns) — but a tab is a button with its own padding, so without this its
          label started 6px inside that gutter and the page read as two left edges.
          Alignment is a baseline, so it belongs here, not in each call site. */}
      {/* `self-stretch` (not just `items-stretch`) is what puts the ACTIVE
          UNDERLINE on the bar's bottom border. `items-stretch` only stretches the
          nav's CHILDREN to the nav's own height — but the bar centres the nav
          itself (`items-center`), so the nav is content-height and the buttons
          stop ~3-4px short of the bar's bottom. The indicator is `absolute
          bottom-0` on the button, so it floats above the border and reads as a
          gap between the blue underline and the grey rule.

          Keep in step with the canonical copy in `@clarix/ui-shared`
          (`page-shell/SwitcherBar.tsx`) until the two are consolidated. */}
      <nav className="-ml-1.5 flex flex-1 items-stretch gap-1.5 self-stretch overflow-x-auto">
        {items.map((item) => {
          const isActive = item.key === value;
          const Icon = item.icon;
          const responsive = item.responsiveLabel ?? true;
          return (
            <button
              key={item.key}
              type="button"
              onClick={() => onChange(item.key)}
              className={cn(
                'hover:text-foreground relative flex items-center gap-1.5 whitespace-nowrap px-1.5 py-2.5 text-lg font-medium transition-colors',
                isActive ? 'text-foreground' : 'text-muted-foreground',
              )}
            >
              {Icon && <Icon className="h-3.5 w-3.5" strokeWidth={1.5} />}
              {item.label && (
                <span className={cn(responsive && 'hidden sm:inline')}>{item.label}</span>
              )}
              {item.count !== undefined && (
                <span
                  className={cn(
                    'rounded-full px-1.5 py-0.5 text-xs font-medium',
                    isActive ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-100 text-gray-600',
                  )}
                >
                  {item.count}
                </span>
              )}
              {isActive && (
                /**
                 * `-bottom-px`, not `bottom-0` — the indicator has to COVER the
                 * bar's bottom border, not stack on top of it.
                 *
                 * `border-b` draws inside the border box, and a stretched flex
                 * child only fills the CONTENT box, so `bottom-0` lands the 2px
                 * indicator directly above the 1px rule: under the active tab you
                 * saw indigo and then a thin grey line under it — one line too
                 * many. Pulling it down by exactly the border width makes the two
                 * occupy the same pixels, so the active tab reads as a single
                 * unbroken underline flush with the rule either side of it.
                 */
                <span className="absolute -bottom-px left-0 right-0 h-0.5 rounded-full bg-indigo-500" />
              )}
            </button>
          );
        })}
      </nav>
      {children && <div className="ml-3 flex shrink-0 items-center gap-2">{children}</div>}
    </div>
  );
}
